import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Target, BookOpen, Wrench, ChevronDown, GraduationCap } from "lucide-react";
import { toast } from "react-toastify";
import SecondaryLoader from "../components/SecondaryLoader/SecondaryLoader";
import PathwayCard from "../components/upskillingPathway/PathwayCard";
import CourseCard from "../components/upskillingPathway/CourseCard";
import PathwayItemCard from "../components/upskillingPathway/PathwayItemCard";
import RecommendedCommunities from "../components/upskillingPathway/RecommendedCommunities";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";
import { setHasPathway } from "../redux/userSlice";
import { updateUserProfilePreferences } from "../services/profileService";

let globalApiRunning = false;

const CollapsibleSection = ({ title, description, icon: Icon, iconBg, isExpanded, onToggle, children }) => (
  <div className="rounded-xl shadow-sm border border-gray-400">
    <button onClick={onToggle} className="w-full p-6 text-left flex items-center justify-between ">
      <div className="flex items-start gap-3">
        <div className={`mt-1 w-10 h-10 bg-[#6e28cc] rounded-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
      </div>
      <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
    </button>
    {isExpanded && <div className="px-6 pb-6">{children}</div>}
  </div>
);

const MyUpskillingPathway = () => {
  const dispatch = useDispatch();
  const { targetJob, currentJob, preferredFormatLearning, skillsGapCount, skillsGap, proficiencyLevel } = useSelector((state) => state.user);
  const { id: userId } = useSelector((state) => state.auth);

  const [expandedSections, setExpandedSections] = useState({
    learningModules: true,
    skillGaps: true,
    aiTools: false,
    certificates: false
  });
  const [pathwayData, setPathwayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isApiRunningRef = useRef(false);
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const fetchPathwayData = async () => {
      try {
        globalApiRunning = true;
        isApiRunningRef.current = true;
        hasRequestedRef.current = true;
        setLoading(true);
        const response = await api1.post(API_ENDPOINTS.UPSKILLING_PATHWAY, {
          currentJob,
          targetJob,
          learningPreferences: preferredFormatLearning || [],
          skillsGapCount: skillsGapCount || 0,
          skillsGap: skillsGap || "Not Given",
          aiLiteracyLevel: proficiencyLevel || "Not Given"
        });
        setPathwayData(response.data);
        dispatch(setHasPathway(true));
        if (userId) {
          await updateUserProfilePreferences(userId, {
            hasPathway: true
          });
        }
        toast.success("Your Upskilling Pathway has been generated successfully!");
      } catch (err) {
        setError(err);
        hasRequestedRef.current = false; // Reset on error so user can retry
      } finally {
        setLoading(false);
        isApiRunningRef.current = false;
        globalApiRunning = false;
      }
    };

    if (currentJob && targetJob && !globalApiRunning && !hasRequestedRef.current) {
      fetchPathwayData();
    } else if (!currentJob || !targetJob) {
      setLoading(false);
      setError(new Error('Current job and target job are required'));
    } else if (globalApiRunning || hasRequestedRef.current) {
      setLoading(true);
    }
  }, [currentJob, targetJob, preferredFormatLearning, skillsGapCount, skillsGap, proficiencyLevel]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <SecondaryLoader message="Your Upskilling Pathway will take 3-5 mins to generate" />
      </div>
    );
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const sections = [
    { key: 'learningModules', title: 'Courses', description: 'Structured courses and projects to achieve your career goals.', icon: BookOpen, iconBg: 'bg-gradient-to-br from-pink-500 to-purple-600', component: CourseCard },
    { key: 'skillGaps', title: 'Summary of Skills To Develop', description: 'Focus areas based on your gap analysis.', icon: Target, iconBg: 'bg-gradient-to-br from-pink-500 to-purple-600', component: PathwayItemCard, type: 'skill' },
    { key: 'aiTools', title: 'AI Tools Integrated in the Courses', description: 'Essential tools for your target career.', icon: Wrench, iconBg: 'bg-gradient-to-br from-pink-500 to-purple-600', component: PathwayItemCard, type: 'aiTool' },
    { key: 'certificates', title: 'Professional Certificates to Target', description: 'Industry-recognized certifications that boost your profile.', icon: GraduationCap, iconBg: 'bg-gradient-to-br from-pink-500 to-purple-600', component: PathwayItemCard, type: 'certificate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 w-12 h-12 bg-[#6e28cc] rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Upskilling Pathway</h1>
              <p className="text-sm text-gray-600">Your personalized journey based on your career decision and assessments.</p>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 space-y-6">
            <PathwayCard/>

            {sections.map(({ key, title, description, icon, iconBg, component: Component, type }) => (
              <CollapsibleSection
                key={key}
                title={title}
                description={description}
                icon={icon}
                iconBg={iconBg}
                isExpanded={expandedSections[key]}
                onToggle={() => toggleSection(key)}
              >
                {Component === PathwayItemCard ? (
                  <Component 
                    items={pathwayData?.[key] || []} 
                    type={type} 
                    title={title} 
                  />
                ) : (
                  <Component pathwayData={pathwayData} />
                )}
              </CollapsibleSection>
            ))}
          </div>

          {/* Right Sidebar */}
          <div className="lg:w-80 space-y-6">
            <RecommendedCommunities />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyUpskillingPathway;
